import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import '@xyflow/react/dist/style.css';
import { ArrowLeft, Loader2, Monitor } from 'lucide-react';
import type { Workflow, WorkflowDefinition, WorkflowNode } from 'shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiGet, apiPatch, apiPost } from '@/lib/api-client';
import {
  NodePalette,
  NodePropertiesPanel,
  WorkflowCanvas,
  WorkflowToolbar,
} from './components/-index';
import type { NodeTemplate } from './components/-node-templates';
import { useWorkflowStore } from './components/-workflow-store';
import { useIsMobile } from '@/hooks/use-mobile';

interface WorkflowResponse extends Workflow {
  definition: WorkflowDefinition;
}

// Default empty definition
const EMPTY_DEFINITION: WorkflowDefinition = {
  nodes: [],
  edges: [],
  viewport: { x: 0, y: 0, zoom: 1 },
};

/** Deduplicate definition nodes and edges by id (keeps first occurrence) */
function dedupeDefinition(def: WorkflowDefinition): WorkflowDefinition {
  const seenNodes = new Set<string>();
  const seenEdges = new Set<string>();
  return {
    ...def,
    nodes: def.nodes.filter((n) => {
      if (seenNodes.has(n.id)) return false;
      seenNodes.add(n.id);
      return true;
    }),
    edges: def.edges.filter((e) => {
      if (seenEdges.has(e.id)) return false;
      seenEdges.add(e.id);
      return true;
    }),
  };
}

function WorkflowBuilderPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { workflowId } = Route.useParams() as { workflowId: string };

  // Local state for workflow
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [definition, setDefinition] = useState<WorkflowDefinition>(EMPTY_DEFINITION);
  const [selectedNode, setSelectedNode] = useState<WorkflowNode | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Fetch workflow data
  const { data: workflow, isLoading } = useQuery({
    queryKey: ['workflow', workflowId],
    queryFn: async () => {
      return apiGet<WorkflowResponse>(`/workflows/${workflowId}`);
    },
    enabled: !!workflowId && workflowId !== 'new',
  });

  // Update local state when workflow data changes
  useEffect(() => {
    if (workflow) {
      setWorkflowName(workflow.name || '');
      setWorkflowDescription(workflow.description || '');
      setDefinition(dedupeDefinition(workflow.definition || EMPTY_DEFINITION));
      setHasChanges(false);
    }
  }, [workflow]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        const { undo, canUndo } = useWorkflowStore.getState();
        if (canUndo()) undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        const { redo, canRedo } = useWorkflowStore.getState();
        if (canRedo()) redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const workflowStatus = workflow?.status ?? 'draft';

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: { definition: WorkflowDefinition }) => {
      if (workflowId === 'new') {
        // Create new workflow first
        return apiPost<WorkflowResponse>('/workflows', {
          name: workflowName || 'New Workflow',
          description: workflowDescription,
          entityType: 'ticket',
          status: 'draft',
          isTemplate: false,
          definition: data.definition,
        });
      }
      return apiPatch<WorkflowResponse>(`/workflows/${workflowId}`, {
        name: workflowName,
        description: workflowDescription,
        definition: data.definition,
      });
    },
    onSuccess: (response) => {
      toast.success('Workflow saved successfully');
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });

      // If we just created a new workflow, navigate to its page
      if (workflowId === 'new' && response?.id) {
        navigate({ to: `/admin/workflows/${response.id}` });
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to save workflow: ${error.message}`);
    },
  });

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: async () => {
      return apiPost<{ message: string; workflow: WorkflowResponse }>(
        `/workflows/${workflowId}/publish`,
        { definition: dedupeDefinition(definition) },
      );
    },
    onSuccess: () => {
      toast.success('Workflow published successfully');
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to publish workflow: ${error.message}`);
    },
  });

  // Handle definition changes
  const handleDefinitionChange = useCallback((newDefinition: WorkflowDefinition) => {
    setDefinition(newDefinition);
    setHasChanges(true);
  }, []);

  // Handle node selection
  const handleNodeSelect = useCallback((node: WorkflowNode | null) => {
    setSelectedNode(node);
  }, []);

  // Handle node update from properties panel
  const handleNodeUpdate = useCallback((nodeId: string, updates: Partial<WorkflowNode>) => {
    setDefinition((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) => (node.id === nodeId ? { ...node, ...updates } : node)),
    }));
    setHasChanges(true);
    // Update selected node reference
    setSelectedNode((prev) => (prev?.id === nodeId ? { ...prev, ...updates } : null));
  }, []);

  // Handle adding a new node from template (preconfigured or generic)
  const handleAddNode = useCallback(
    (template: NodeTemplate) => {
      const id = crypto.randomUUID();
      const newNode: WorkflowNode = {
        id,
        type: template.nodeType,
        position: {
          x: 100 + definition.nodes.length * 50,
          y: 100 + definition.nodes.length * 50,
        },
        data: {
          label: template.label,
          description: template.description,
          config: { ...template.config },
        },
      };
      setDefinition((prev) => ({
        ...prev,
        nodes: [...prev.nodes, newNode],
      }));
      setHasChanges(true);
    },
    [definition.nodes.length],
  );

  // Handle save
  const handleSave = useCallback(async () => {
    saveMutation.mutate({ definition: dedupeDefinition(definition) });
  }, [definition, saveMutation]);

  // Handle publish
  const handlePublish = useCallback(async () => {
    publishMutation.mutate();
  }, [publishMutation]);

  // Handle validate
  const handleValidate = useCallback(async () => {
    try {
      const result = await apiPost<{
        valid: boolean;
        errors: Array<{ message: string }>;
        errorCount: number;
        warningCount: number;
      }>('/workflows/validate', { definition });

      setIsValid(result.valid);
      if (result.valid) {
        setValidationError(null);
        toast.success('Workflow is valid');
      } else {
        const errorMessages = result.errors.map((e) => e.message).join(', ');
        setValidationError(errorMessages || 'Workflow has validation errors');
        toast.error(`Validation failed: ${errorMessages}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Validation failed';
      setIsValid(false);
      setValidationError(message);
      toast.error(message);
    }
  }, [definition]);

  // Handle test
  const handleTest = useCallback(() => {
    toast.info('Test mode coming soon');
  }, []);

  const isSaving = saveMutation.isPending;
  const isPublishing = publishMutation.isPending;

  // Mobile guard
  if (isMobile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <Monitor className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Best Viewed on Desktop</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            The workflow builder requires a larger screen for the best experience. Please switch to
            a desktop or laptop to use this feature.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate({ to: '/home' })}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
      </div>
    );
  }

  // Show loading state for existing workflows
  if (workflowId !== 'new' && isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading workflow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen min-h-0 -mx-3 -my-3 md:-mx-4 md:-my-4">
      {/* Header: back, name, description, toolbar - always visible */}
      <header className="flex flex-col shrink-0 border-b bg-background">
        <div className="flex items-center gap-2 px-3 py-2 min-h-12">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 h-8 w-8"
            onClick={() => navigate({ to: '/admin/workflows' })}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0 flex flex-col justify-center py-0.5">
            <div className="flex items-center gap-2">
              <Input
                id="workflow-name"
                value={workflowName}
                onChange={(e) => {
                  setWorkflowName(e.target.value);
                  setHasChanges(true);
                }}
                placeholder="Workflow name..."
                className="h-7 text-sm font-semibold border-0 bg-transparent shadow-none focus-visible:ring-1 rounded px-1.5 -ml-1 min-w-[100px] max-w-sm placeholder:text-muted-foreground/60"
              />
              <Badge variant="outline" className="shrink-0 text-[10px] px-1.5 py-0 capitalize">
                {workflowStatus}
              </Badge>
              {hasChanges && (
                <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                  Unsaved
                </span>
              )}
            </div>
            <Input
              id="workflow-description"
              value={workflowDescription}
              onChange={(e) => {
                setWorkflowDescription(e.target.value);
                setHasChanges(true);
              }}
              placeholder="Add description..."
              className="h-6 text-xs text-muted-foreground border-0 bg-transparent shadow-none focus-visible:ring-1 rounded px-1.5 -ml-1 mt-0.5 placeholder:text-muted-foreground/50"
            />
          </div>
          <WorkflowToolbar
            showStatusBadge={false}
            compact
            onSave={handleSave}
            onPublish={handlePublish}
            onValidate={handleValidate}
            onTest={handleTest}
            hasChanges={hasChanges}
            isValid={isValid}
            validationError={validationError}
            isSaving={isSaving}
            isPublishing={isPublishing}
            status={workflowStatus}
          />
        </div>
        {!isValid && validationError && (
          <div className="px-4 py-1.5 bg-destructive/10 text-destructive text-xs border-t border-destructive/20">
            {validationError}
          </div>
        )}
      </header>

      {/* Main content area - takes remaining height */}
      <div className="flex-1 flex min-h-0">
        <div className="w-72 shrink-0 border-r bg-muted/20 flex flex-col min-h-0">
          <NodePalette onAddNode={handleAddNode} />
        </div>
        <div className="flex-1 min-w-0 relative">
          <WorkflowCanvas
            key={workflowId}
            definition={definition}
            onChange={handleDefinitionChange}
            onNodeSelect={handleNodeSelect}
          />
        </div>
        <div className="w-80 shrink-0 border-l flex flex-col min-h-0">
          <NodePropertiesPanel node={selectedNode} onUpdate={handleNodeUpdate} />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/admin/workflows/$workflowId')({
  component: WorkflowBuilderPage,
});
